import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    uploadCourses,
    resetStatus as resetCourseStatus
} from "../../Redux/CourseDetails";
import {
    uploadLearnersReport,
    resetStatus as resetLearnerStatus
} from "../../Redux/LearnersReport";
import {
    uploadUserData,
    resetStatus as resetUserStatus
} from "../../Redux/UserDetails";
import "./Admin.css";

const Admin = () => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const courseState = useSelector((state) => state.courseDetails);
    const learnerState = useSelector((state) => state.learnersReport);
    const userState = useSelector((state) => state.userDetails);

    const [activeTab, setActiveTab] = useState("courses");
    const [selectedHub, setSelectedHub] = useState("");
    const [file, setFile] = useState(null);
    const [uiError, setUiError] = useState("");
    const [uiSuccess, setUiSuccess] = useState("");

    const [hasSubmitted, setHasSubmitted] = useState(false);

    const loader =
        activeTab === "courses"
            ? courseState?.loader
            : activeTab === "learners"
                ? learnerState?.loader
                : userState?.loader;

    const error =
        activeTab === "courses"
            ? courseState?.error
            : activeTab === "learners"
                ? learnerState?.error
                : userState?.error;

    const handleSubmit = () => {
        setUiError("");
        setUiSuccess("");

        if (!file) {
            setUiError("Please select an Excel file.");
            return;
        }

        if (activeTab === "courses") {
            if (!selectedHub) {
                setUiError("Please select a hub.");
                return;
            }
            setHasSubmitted(true);
            dispatch(uploadCourses(selectedHub, file));
        } else if (activeTab === "learners") {
            setHasSubmitted(true);
            dispatch(uploadLearnersReport(file));
        } else if (activeTab === "users") {
            setHasSubmitted(true);
            dispatch(uploadUserData(file));
        }
    };

    useEffect(() => {
        if (!hasSubmitted) return;

        const currentState =
            activeTab === "courses"
                ? courseState
                : activeTab === "learners"
                    ? learnerState
                    : userState;

        if (currentState.loader) return;

        if (currentState.error) {
            setUiError(currentState.error);
        } else {
            setUiSuccess(
                activeTab === "courses"
                    ? "Courses uploaded successfully."
                    : activeTab === "learners"
                        ? "Learners report uploaded successfully."
                        : "User data uploaded successfully."
            );
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            setSelectedHub("");
        }
        setHasSubmitted(false);
    }, [
        hasSubmitted,
        activeTab,
        courseState.loader,
        courseState.error,
        learnerState.loader,
        learnerState.error,
        userState.loader,
        userState.error
    ]);

    // Auto-clear messages (unchanged)
    useEffect(() => {
        if (uiError || uiSuccess) {
            const timer = setTimeout(() => {
                setUiError("");
                setUiSuccess("");
                activeTab === "courses"
                    ? dispatch(resetCourseStatus())
                    : activeTab === "learners"
                        ? dispatch(resetLearnerStatus())
                        : dispatch(resetUserStatus());
            }, 4000);

            return () => clearTimeout(timer);
        }
    }, [uiError, uiSuccess, dispatch, activeTab]);

    // Clear messages on input change (unchanged)
    useEffect(() => {
        if (file || selectedHub) {
            setUiError("");
            setUiSuccess("");
        }
    }, [file, selectedHub]);

    return (
        <div className="upload-wrapper">
            {/* LEFT TABS */}
            <div className="upload-tabs">
                <div
                    className={`tab ${activeTab === "courses" ? "active" : ""}`}
                    onClick={() => {
                        setActiveTab("courses");
                        setFile(null);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                        }
                        setUiError("");
                        setUiSuccess("");
                    }}
                >
                    Upload Courses
                </div>

                <div
                    className={`tab ${activeTab === "learners" ? "active" : ""}`}
                    onClick={() => {
                        setActiveTab("learners");
                        setSelectedHub("");
                        setFile(null);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                        }
                        setUiError("");
                        setUiSuccess("");
                    }}
                >
                    Upload Learners Report
                </div>

                <div
                    className={`tab ${activeTab === "users" ? "active" : ""}`}
                    onClick={() => {
                        setActiveTab("users");
                        setSelectedHub("");
                        setFile(null);
                        if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                        }
                        setUiError("");
                        setUiSuccess("");
                    }}
                >
                    Upload User Data
                </div>
            </div>

            {/* CONTENT */}
            <div className="upload-content">
                <h2>
                    {activeTab === "courses"
                        ? "Upload Courses File"
                        : "Upload Learners Report File"}
                </h2>

                {/* HUB SELECT */}
                {activeTab === "courses" && (
                    <div className="form-row">
                        <label>Select the hub to upload file :</label>
                        <select
                            value={selectedHub}
                            onChange={(e) => setSelectedHub(e.target.value)}
                            disabled={loader}
                        >
                            <option value="" disabled>
                                Select Hub
                            </option>
                            <option value="Foundation">Core Hub</option>
                            <option value="GenAI and Agentic AI Hub">
                                GenAI and Agentic AI Hub
                            </option>
                            <option value="Delivery Modernisation Hub">
                                Delivery Modernisation Hub
                            </option>
                            <option value="Future of Experience Hub">
                                Future of Experience Hub
                            </option>
                            <option value="Sales & Solutioning Hub">
                                Sales & Solutioning Hub
                            </option>
                            <option value="Product Hub">Product Hub</option>
                        </select>
                    </div>
                )}

                {/* FILE INPUT */}
                <div className="form-row">
                    <label>Select File:</label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        disabled={loader}
                        onChange={(e) => setFile(e.target.files[0])}
                    />
                    <span className="file-name">
                        {file ? file.name : "No File Selected"}
                    </span>
                </div>

                <button
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={loader}
                >
                    {loader ? "Uploading..." : "SUBMIT"}
                </button>

                {/* UI ERROR */}
                {uiError && <div className="error-msg">{uiError}</div>}

                {/* UI SUCCESS */}
                {uiSuccess && <div className="success-msg">{uiSuccess}</div>}
            </div>
        </div>
    );
};

export default Admin;